import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import api from '@/lib/api';
import { TOKEN_KEY } from '@/lib/apiClient';
import { normalizeRole as canonicalizeRole } from '@/lib/roleRedirects';
export const useAuthStore = defineStore('auth', () => {
    const user = ref(null);
    const isAuthenticated = ref(false);
    const isLoading = ref(false);
    const error = ref(null);
    const loginChallenge = ref(null);
    const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
    let inactivityTimer = null;
    let activityListenersAttached = false;
    let activityHandler = null;
    const CHALLENGE_KEY = 'auth_challenge';
    const clearInactivityTimer = () => {
        if (inactivityTimer !== null) {
            window.clearTimeout(inactivityTimer);
            inactivityTimer = null;
        }
    };
    const persistLoginChallenge = () => {
        if (typeof window === 'undefined')
            return;
        if (!loginChallenge.value) {
            window.sessionStorage.removeItem(CHALLENGE_KEY);
            return;
        }
        window.sessionStorage.setItem(CHALLENGE_KEY, JSON.stringify(loginChallenge.value));
    };
    const loadLoginChallenge = () => {
        if (typeof window === 'undefined')
            return null;
        const raw = window.sessionStorage.getItem(CHALLENGE_KEY);
        if (!raw)
            return null;
        try {
            return JSON.parse(raw);
        }
        catch {
            window.sessionStorage.removeItem(CHALLENGE_KEY);
            return null;
        }
    };
    const clearLoginChallenge = () => {
        loginChallenge.value = null;
        if (typeof window !== 'undefined') {
            window.sessionStorage.removeItem(CHALLENGE_KEY);
        }
    };
    const resetSessionTimer = () => {
        if (!isAuthenticated.value || typeof window === 'undefined')
            return;
        clearInactivityTimer();
        inactivityTimer = window.setTimeout(() => {
            void logout().finally(() => {
                window.location.assign('/login?expired=1');
            });
        }, SESSION_TIMEOUT_MS);
    };
    const attachActivityListeners = () => {
        if (typeof window === 'undefined' || activityListenersAttached)
            return;
        activityHandler = () => {
            if (isAuthenticated.value) {
                resetSessionTimer();
            }
        };
        window.addEventListener('mousemove', activityHandler, { passive: true });
        window.addEventListener('keydown', activityHandler, { passive: true });
        window.addEventListener('click', activityHandler, { passive: true });
        window.addEventListener('touchstart', activityHandler, { passive: true });
        activityListenersAttached = true;
    };
    const detachActivityListeners = () => {
        if (typeof window === 'undefined' || !activityListenersAttached || !activityHandler)
            return;
        window.removeEventListener('mousemove', activityHandler);
        window.removeEventListener('keydown', activityHandler);
        window.removeEventListener('click', activityHandler);
        window.removeEventListener('touchstart', activityHandler);
        activityHandler = null;
        activityListenersAttached = false;
    };
    const getAvailableDashboardViews = () => {
        const userData = user.value;
        const rawRoles = [
            userData?.role_slug,
            userData?.role,
            userData?.role_name,
            ...(Array.isArray(userData?.roles) ? userData.roles : []),
        ].filter(Boolean).map((value) => canonicalizeRole(String(value)));
        const uniqueRoles = Array.from(new Set(rawRoles.filter(Boolean)));
        const views = [];
        if (uniqueRoles.includes('dean')) {
            views.push('dean');
            // A dean assigned to a department should be able to toggle between the
            // dean workspace and the faculty workspace for that department.
            if (!uniqueRoles.includes('faculty')) {
                views.push('faculty');
            }
        }
        if (uniqueRoles.includes('program-chair')) {
            views.push('program-chair');
            // Program chairs who assign themselves as Area Chair or member
            // need the faculty/area-task workspace as well.
            if (!uniqueRoles.includes('faculty')) {
                views.push('faculty');
            }
        }
        if (uniqueRoles.includes('area-in-charge')) {
            views.push('area-in-charge');
            views.push('faculty');
        }
        if (uniqueRoles.includes('faculty'))
            views.push('faculty');
        if (uniqueRoles.includes('qa'))
            views.push('qa');
        if (uniqueRoles.includes('vpaa'))
            views.push('vpaa');
        if (uniqueRoles.includes('superadmin') || uniqueRoles.includes('admin')) {
            views.push('superadmin');
        }
        if (views.length === 0 && (userData?.programId || userData?.program_id || userData?.program?.id)) {
            views.push('faculty');
        }
        return Array.from(new Set(views));
    };
    const getStoredDashboardView = () => {
        if (typeof window === 'undefined')
            return '';
        const saved = window.localStorage.getItem('role_dashboard_view');
        const normalized = saved ? canonicalizeRole(saved) : '';
        return normalized;
    };
    const dashboardView = ref(getStoredDashboardView());
    // canonicalize role using central helper, while allowing a per-session interface switch
    const userRole = computed(() => {
        const raw = user.value?.role_slug || user.value?.role || '';
        const base = canonicalizeRole(String(raw));
        const available = getAvailableDashboardViews();
        if (dashboardView.value && available.includes(dashboardView.value)) {
            return dashboardView.value;
        }
        const preferredOrder = [
            'dean',
            'program-chair',
            'area-in-charge',
            'faculty',
            'qa',
            'vpaa',
            'superadmin',
            'admin',
        ];
        const candidates = Array.from(new Set([base, ...available].filter(Boolean)));
        const selected = preferredOrder.find((role) => candidates.includes(role));
        return selected || base || (available[0] ?? '');
    });
    const isSuperAdmin = computed(() => userRole.value === 'superadmin' || userRole.value === 'admin');
    const isQA = computed(() => userRole.value === 'qa');
    const isVPAA = computed(() => userRole.value === 'vpaa');
    const isDean = computed(() => userRole.value === 'dean');
    const isFaculty = computed(() => userRole.value === 'faculty');
    const isAreaIncharge = computed(() => userRole.value === 'area-in-charge');
    const isProgramChair = computed(() => userRole.value === 'program-chair');
    const availableDashboardViews = computed(() => getAvailableDashboardViews());
    const canViewAs = (view) => availableDashboardViews.value.includes(view);
    const setDashboardView = (view) => {
        if (!canViewAs(view))
            return;
        dashboardView.value = view;
        if (typeof window !== 'undefined') {
            window.localStorage.setItem('role_dashboard_view', view);
        }
    };
    const setDefaultDashboardViewForRole = () => {
        if (!user.value || typeof window === 'undefined')
            return;
        const available = getAvailableDashboardViews();
        const defaultOrder = [
            'dean',
            'program-chair',
            'faculty',
        ];
        const preferred = defaultOrder.find((role) => available.includes(role));
        if (preferred) {
            dashboardView.value = preferred;
            window.localStorage.setItem('role_dashboard_view', preferred);
            return;
        }
        if (available.includes('area-in-charge')) {
            dashboardView.value = 'area-in-charge';
            window.localStorage.setItem('role_dashboard_view', 'area-in-charge');
            return;
        }
        if (available.includes('qa')) {
            dashboardView.value = 'qa';
            window.localStorage.setItem('role_dashboard_view', 'qa');
            return;
        }
        if (available.includes('vpaa')) {
            dashboardView.value = 'vpaa';
            window.localStorage.setItem('role_dashboard_view', 'vpaa');
            return;
        }
    };
    const clearDashboardView = () => {
        dashboardView.value = '';
        if (typeof window !== 'undefined') {
            window.localStorage.removeItem('role_dashboard_view');
        }
    };
    const userName = computed(() => user.value?.name || '');
    const normalizeUserProgramContext = (userData) => {
        if (!userData || typeof userData !== 'object')
            return userData;
        const programId = userData.programId ??
            userData.program_id ??
            userData.chaired_program_id ??
            userData.program?.id ??
            userData.program?.program_id ??
            null;
        if (programId !== null && programId !== undefined) {
            userData.programId = String(programId);
            userData.program_id = String(programId);
        }
        if (!userData.program && programId !== null && programId !== undefined) {
            userData.program = { id: String(programId) };
        }
        if (!userData.programId && userData.program?.id) {
            userData.programId = String(userData.program.id);
        }
        if (!userData.program_id && userData.program?.id) {
            userData.program_id = String(userData.program.id);
        }
        return userData;
    };
    const hasGroup = computed(() => {
        if (!user.value)
            return false;
        const currentUser = user.value;
        if (!currentUser)
            return false;
        if (currentUser.programId || currentUser.program_id || currentUser.program?.id)
            return true;
        if (currentUser.teamId)
            return true;
        if (Array.isArray(currentUser.groups) && currentUser.groups.length > 0)
            return true;
        return false;
    });
    // ======================
    // LOGIN
    // ======================
    const setToken = (token, remember) => {
        if (typeof window === 'undefined')
            return;
        window.localStorage.removeItem(TOKEN_KEY);
        window.sessionStorage.removeItem(TOKEN_KEY);
        if (remember) {
            window.localStorage.setItem(TOKEN_KEY, token);
        }
        else {
            window.sessionStorage.setItem(TOKEN_KEY, token);
        }
    };
    const removeToken = () => {
        if (typeof window === 'undefined')
            return;
        window.localStorage.removeItem(TOKEN_KEY);
        window.sessionStorage.removeItem(TOKEN_KEY);
    };
    const login = async (email, password, remember = false) => {
        isLoading.value = true;
        error.value = null;
        try {
            const response = await api.post('/login', {
                email,
                password,
            });
            // DISABLED: 2FA code entry — backend now returns token immediately, see [2026-08-18]
            // Check if backend returned a token directly (2FA bypassed) or a challenge token (old flow)
            const token = response.data.data.token;
            if (token) {
                // New flow: backend returned token immediately (2FA is skipped)
                setToken(token, remember);
                const userData = response.data.data.user;
                if (userData) {
                    user.value = normalizeUserProgramContext(userData);
                }
                else {
                    await restoreSession();
                }
                if (user.value) {
                    const savedView = getStoredDashboardView();
                    const available = getAvailableDashboardViews();
                    if (savedView && !available.includes(savedView)) {
                        clearDashboardView();
                    }
                    if (!dashboardView.value || !available.includes(dashboardView.value)) {
                        setDefaultDashboardViewForRole();
                    }
                    isAuthenticated.value = true;
                    attachActivityListeners();
                    resetSessionTimer();
                }
                clearLoginChallenge();
            }
            else {
                // Old flow: backend returned challenge token (2FA still enabled)
                loginChallenge.value = {
                    challengeToken: response.data.data.challenge_token,
                    email: response.data.data.email,
                    expiresIn: response.data.data.expires_in,
                    remember,
                };
                persistLoginChallenge();
            }
            return response.data;
        }
        catch (err) {
            const responseMessage = err.response?.data?.message;
            const emailError = err.response?.data?.errors?.email?.[0];
            const passwordError = err.response?.data?.errors?.password?.[0];
            const serverMessage = emailError || passwordError || responseMessage || 'Login failed.';
            error.value = serverMessage;
            throw new Error(serverMessage);
        }
        finally {
            isLoading.value = false;
        }
    };
    // DISABLED: 2FA verification — skipped in login, see [2026-08-18]
    // This method is kept as a stub to avoid breaking any lingering frontend calls
    // Original implementation commented out:
    // const verifyTwoFactor = async (code: string) => {
    //   if (!loginChallenge.value) { ... }
    //   const response = await api.post('/auth/verify-2fa', { ... })
    //   ...
    // }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- param kept so the signature stays stable when 2FA is re-enabled
    const verifyTwoFactor = async (code) => {
        error.value = '2FA verification is skipped for development. The login endpoint now returns a token directly.';
        throw new Error(error.value);
    };
    // DISABLED: 2FA resend — skipped in login, see [2026-08-18]
    // This method is kept as a stub to avoid breaking any lingering frontend calls
    // Original implementation commented out:
    // const resendTwoFactor = async () => {
    //   if (!loginChallenge.value) { ... }
    //   const response = await api.post('/auth/resend-2fa', { ... })
    //   ...
    // }
    const resendTwoFactor = async () => {
        error.value = '2FA resend is skipped for development. The login endpoint now returns a token directly.';
        throw new Error(error.value);
    };
    // ======================
    // REGISTER
    // ======================
    const register = async (data) => {
        isLoading.value = true;
        error.value = null;
        try {
            const config = data instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined;
            const response = await api.post('/register', data, config);
            // Public registration requires email verification first; do not auto-authenticate the new account.
            return response.data;
        }
        catch (err) {
            error.value =
                err.response?.data?.message || 'Registration failed';
            throw err;
        }
        finally {
            isLoading.value = false;
        }
    };
    // ======================
    // GET CURRENT USER
    // ======================
    const restoreSession = async () => {
        if (!loginChallenge.value) {
            loginChallenge.value = loadLoginChallenge();
        }
        const token = window.localStorage.getItem(TOKEN_KEY) ||
            window.sessionStorage.getItem(TOKEN_KEY);
        if (!token) {
            user.value = null;
            isAuthenticated.value = false;
            return;
        }
        try {
            const response = await api.get('/me');
            user.value = normalizeUserProgramContext(response.data.data.user);
            const savedView = getStoredDashboardView();
            const available = getAvailableDashboardViews();
            if (savedView && !available.includes(savedView)) {
                clearDashboardView();
            }
            if (!dashboardView.value || !available.includes(dashboardView.value)) {
                setDefaultDashboardViewForRole();
            }
            isAuthenticated.value = true;
            attachActivityListeners();
            resetSessionTimer();
            return response.data;
        }
        catch {
            localStorage.removeItem(TOKEN_KEY);
            user.value = null;
            isAuthenticated.value = false;
        }
    };
    const refreshCurrentUser = async () => {
        if (typeof window === 'undefined')
            return;
        const token = window.localStorage.getItem(TOKEN_KEY) ||
            window.sessionStorage.getItem(TOKEN_KEY);
        if (!token)
            return;
        try {
            const response = await api.get('/me');
            user.value = normalizeUserProgramContext(response.data.data.user);
            isAuthenticated.value = true;
            return response.data;
        }
        catch {
            localStorage.removeItem(TOKEN_KEY);
            user.value = null;
            isAuthenticated.value = false;
        }
    };
    // Join a team using invitation code
    const joinTeam = async (code) => {
        isLoading.value = true;
        error.value = null;
        try {
            const response = await api.post('/teams/join', { code });
            // After joining, refresh current user data
            await restoreSession();
            return response.data;
        }
        catch (err) {
            error.value = err.response?.data?.message || 'Failed to join team.';
            throw err;
        }
        finally {
            isLoading.value = false;
        }
    };
    // Join a team using the 6-character team code
    const acceptInvitation = async (value) => {
        isLoading.value = true;
        error.value = null;
        try {
            const input = value.trim();
            if (!input) {
                throw new Error('Team code is required.');
            }
            if (!/^[A-Z0-9]{6}$/i.test(input)) {
                throw new Error('Enter the 6-character team code from your Program Chair.');
            }
            const response = await api.post('/teams/join', { code: input.toUpperCase() });
            await restoreSession();
            return response.data;
        }
        catch (err) {
            error.value = err.response?.data?.message || err.message || 'Failed to join team.';
            throw err;
        }
        finally {
            isLoading.value = false;
        }
    };
    // ======================
    // LOGOUT
    // ======================
    const logout = async () => {
        isLoading.value = true;
        try {
            await api.post('/logout');
        }
        catch (e) {
            console.log(e);
        }
        removeToken();
        clearLoginChallenge();
        clearDashboardView();
        user.value = null;
        isAuthenticated.value = false;
        clearInactivityTimer();
        detachActivityListeners();
        isLoading.value = false;
    };
    // ======================
    // DISABLED
    // ======================
    const loginWithGoogle = async () => {
        throw new Error('Google login is not implemented.');
    };
    const loginWithGithub = async () => {
        throw new Error('GitHub login is not implemented.');
    };
    const setupAuthListener = () => { };
    const cleanupAuthListener = () => { };
    return {
        user,
        isAuthenticated,
        isLoading,
        error,
        userRole,
        userName,
        hasGroup,
        isSuperAdmin,
        isQA,
        isVPAA,
        isDean,
        isFaculty,
        isAreaIncharge,
        isProgramChair,
        availableDashboardViews,
        canViewAs,
        setDashboardView,
        setDefaultDashboardViewForRole,
        clearDashboardView,
        login,
        verifyTwoFactor,
        resendTwoFactor,
        register,
        logout,
        restoreSession,
        refreshCurrentUser,
        joinTeam,
        acceptInvitation,
        loginWithGoogle,
        loginWithGithub,
        setupAuthListener,
        cleanupAuthListener,
    };
});
